import { TextWritingType } from '@meitu/saas-editor-core/es/version-types';
import { checkFontIdIsTeamFontId } from '@meitu/xdesign-widgets';
import { type FontItem } from '@/components/font-family-selector';
import { baseTrack, type BaseTrackPayload } from './base';

type Payload = Omit<BaseTrackPayload, 'funcId'>;

export const FontBaseTracker = {
    /**
     * 打开字体选择列表
     */
    openFontFamilySelector(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_7' };
        baseTrack(basePayload, ['click']);
    },

    /**
     * 设置字体
     */
    setFontFamily(payload: Payload & { font: FontItem; isSearch: boolean; isVectorLayer?: boolean }) {
        const { font, isSearch, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: `1_${checkFontIdIsTeamFontId(font.id ?? 0) ? 107 : 7}`,
        };

        baseTrack(basePayload, ['use']);

        window._stat_track('hb_text_edit_yes_data', {
            ['样式字体']: font.id,
            is_search: isSearch ? '1' : '0',
            is_svg_material: isVectorLayer ? '1' : '0',
        });

        window._stat_track('tpbj_material_click', {
            type_id: 1001,
            material_id: font.material_id,
        });

        window._stat_track('tpbj_material_tick', {
            type_id: 1001,
            material_id: font.material_id,
        });
    },

    /**
     * 打开字号选择列表
     */
    openFontSizeSelector(payload: Payload) {
        const basePayload = {
            ...payload,
            funcId: '1_8',
        };

        baseTrack(basePayload, ['click']);
    },

    /**
     * 设置字号
     */
    setFontSize(payload: Payload & { fontSize: number; isVectorLayer?: boolean }) {
        const { fontSize, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: '1_8',
        };

        baseTrack(basePayload, ['click', 'use']);

        window._stat_track('hb_text_edit_yes_data', {
            ['样式字号']: fontSize,
            is_svg_material: isVectorLayer ? '1' : '0',
        });
    },

    /**
     * 打开文字颜色选择器
     */
    openTextColorPicker(payload: Payload & { isFill?: boolean }) {
        const { isFill = false, ...rest } = payload;
        const basePayload = { ...rest, funcId: isFill ? '1_18' : '1_2' };
        baseTrack(basePayload, ['click']);
    },

    /**
     * 设置文字颜色
     */
    setTextColor(payload: Payload & { color: string; isFill?: boolean; isVectorLayer?: boolean }) {
        const { color, isFill = false, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: isFill ? '1_18' : '1_2',
        };

        baseTrack(basePayload, ['use']);

        window._stat_track('hb_text_edit_yes_data', {
            ['字体颜色']: color,
            is_svg_material: isVectorLayer ? '1' : '0',
        });
    },

    /**
     * 设置文字装饰（粗体、斜体、下划线、删除线、书写方向）
     */
    setFontStyle(
        payload: Payload & {
            type: 'bold' | 'italic' | 'underline' | 'lineThrough' | 'writingMode';
            value: boolean | string;
            isVectorLayer?: boolean;
        },
    ) {
        const funcIDs = {
            bold: '1_9',
            italic: '1_10',
            underline: '1_11',
            lineThrough: '1_12',
            writingMode: '1_14',
        };

        const { type, value, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: funcIDs[type],
        };

        baseTrack(basePayload, ['click', 'use']);

        const keys = {
            bold: '样式粗体',
            italic: '样式斜体',
            underline: '样式下划线',
            lineThrough: '样式删除线',
            writingMode: '样式方向',
        };
        const key = keys[type];

        const params: Record<string, string> = {
            is_svg_material: isVectorLayer ? '1' : '0',
        };

        if (type === 'writingMode') {
            params[key] = (value as string) === 'vertical-rl' ? '竖向' : '横向';
        } else {
            params[key] = (value as boolean) ? '是' : '否';
        }

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 设置文字间距
     */
    setSpacing(
        payload: Payload & {
            type: 'lineSpacing' | 'letterSpacing';
            value: number;
            isVectorLayer?: boolean;
        },
    ) {
        const { type, value, isVectorLayer = false, ...rest } = payload;
        const funcId = type === 'letterSpacing' ? '1_15' : '1_16';
        const basePayload = {
            ...rest,
            funcId,
        };

        baseTrack(basePayload, ['click', 'use']);

        const keys = {
            lineSpacing: '样式行间距',
            letterSpacing: '样式字间距',
        };
        const key = keys[type];

        const params = {
            [key]: value,
            is_svg_material: isVectorLayer ? '1' : '0',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 设置透明度
     */
    setTextOpacity(payload: Payload & { opacity: number; isVectorLayer?: boolean }) {
        const { opacity, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: '1_21',
        };

        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['不透明度']: opacity,
            is_svg_material: isVectorLayer ? '1' : '0',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 设置对齐方式
     */
    setAlignment(
        payload: Payload & { writingMode: TextWritingType; type: string; value: string; isVectorLayer?: boolean },
    ) {
        const { writingMode, type, value, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: '1_13',
        };

        baseTrack(basePayload, ['click', 'use']);

        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        // --- 水平对齐 ---
        if (type === 'textAlign') {
            const k = '样式水平对齐';
            let v = '两端对齐';

            switch (value) {
                case 'left':
                    v = isHorizontal ? '左对齐' : '顶对齐';
                    break;
                case 'right':
                    v = isHorizontal ? '右对齐' : '底对齐';
                    break;
                case 'center':
                    v = isHorizontal ? '居中对齐' : '垂直居中';
                    break;
                case 'justify':
                    v = '两端对齐';
                    break;
            }

            const params = {
                [k]: v,
                is_svg_material: isVectorLayer ? '1' : '0',
            };
            window._stat_track('hb_text_edit_yes_data', params);
            return;
        }

        // --- 竖直对齐 ---
        const k = '样式竖直对齐';
        let v = '垂直居中';

        switch (value) {
            case 'top':
                v = isHorizontal ? '顶对齐' : '左对齐';
                break;
            case 'vertical-center':
                v = isHorizontal ? '垂直居中' : '居中对齐';
                break;
            case 'bottom':
                v = isHorizontal ? '底对齐' : '右对齐';
                break;
        }

        const params = {
            [k]: v,
            is_svg_material: isVectorLayer ? '1' : '0',
        };
        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 设置列表文字
     */
    setListText(payload: Payload & { listStyle: string; isVectorLayer?: boolean }) {
        const { listStyle, isVectorLayer = false, ...rest } = payload;
        const basePayload = {
            ...rest,
            funcId: '1_165',
        };

        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['列表格式']: listStyle,
            is_svg_material: isVectorLayer ? '1' : '0',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },
};
